import React, { useState, useEffect } from "react";
import { useAuthContext } from "hooks/useAuthContext";
import { IconButton, CircularProgress } from "@mui/material";
import { ArrowBack, AttachFile } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { API_URL } from "../../../../config";
import { toast } from "sonner";

const AddProgress = ({ onSubmit, onCancel, submitData }) => {
  const { user } = useAuthContext();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTitle(submitData.title);
  }, [submitData.title]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const allowedExtensions = /\.(doc|docx)$/i;

    if (!selectedFile) return;

    if (!allowedExtensions.exec(selectedFile.name)) {
      toast.error("Invalid file type. Please upload a .doc or .docx file.");
      setFile(null);
      event.target.value = "";
    } else {
      setFile(selectedFile);
    }
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("to", user.project.supervisorEmail);
    formData.append("from", user.email);
    formData.append("projectId", user.project.id);

    fetch(`${API_URL}/files/upload`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setLoading(false);

        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success("File uploaded successfully!");
          setFile(null);
          setDescription("");
          onCancel();
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        toast.error("Error uploading file. Please try again.");
      });
  };

  return (
    <MDBox
      color="white"
      bgColor="transparent"
      borderRadius="lg"
      shadow="lg"
      p={3}
    >
      <MDBox display="flex" alignItems="center">
        <IconButton onClick={onCancel}>
          <ArrowBack />
        </IconButton>
        <MDTypography variant="h5" ml={1}>
          Add New Progress
        </MDTypography>
      </MDBox>

      <MDBox>
        <MDInput
          label="Title"
          value={title}
          variant="outlined"
          fullWidth
          sx={{ width: "100%" }}
          disabled
        />
        <MDInput
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          style={{ marginTop: "8px", marginBottom: "16px" }}
        />

        <input
          type="file"
          id="document-upload"
          accept=".doc,.docx"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <label htmlFor="document-upload">
          <MDButton
            variant="outlined"
            component="span"
            startIcon={<AttachFile />}
            color="info"
          >
            Upload Document
          </MDButton>
        </label>
        {file && (
          <MDTypography variant="body2" mt={2}>
            Selected File: {file.name}
          </MDTypography>
        )}
      </MDBox>

      <MDBox mt={3} textAlign="center">
        <MDButton
          variant="contained"
          color="info"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit Document"
          )}
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddProgress;
